/**
 * CardsDeviceList Component
 * 
 * Displays a comprehensive registered device card with detailed information including:
 * - Device icon/image
 * - Device name and ID
 * - Device type and OS badges
 * - MAC address
 * - Status (active/inactive)
 * - Registration date and registered by
 * - Firmware version
 * - Installation date
 * - Last seen timestamp
 * - Sensor and I/O counts
 * - Action buttons (edit, delete)
 * 
 * Responsive layout handled via CSS flex-wrap for dynamic sizing.
 * 
 * @param {Object} device - The registered device data object
 */
import React, { useState, useEffect } from "react";

import { firebaseStore } from "../../../../components/firebase/firestore";
import { deleteDoc, doc, getDoc } from "firebase/firestore";
import { 
  IconButton, 
  Snackbar, 
  Paper,
  Chip,
  Tooltip,
  Box,
  Typography,
  Divider
} from "@mui/material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SensorsIcon from "@mui/icons-material/Sensors";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import RouterIcon from "@mui/icons-material/Router";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FingerprintIcon from "@mui/icons-material/Fingerprint";

import EditDevice from "./EditDevice";
import moment from "moment";

import "./CardsDeviceList.css";

function CardsDeviceList({ device }: any) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [deviceEnum, setDeviceEnum] = useState<any>(null);
  const [showImageFallback, setShowImageFallback] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const style = {
    paper: {
      flex: "1 1 calc(50% - 32px)",
      minWidth: "320px",
      maxWidth: "calc(50% - 32px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
      padding: "4px",
      transition: "all 0.2s ease-in-out",
      border: device.status === "active" ? "1px solid #e0e0e0" : "1px solid #ffcc00",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(68, 169, 154, 0.2)",
        transform: "translateY(-2px)"
      },
      "@media (max-width: 800px)": {
        flex: "1 1 100%",
        maxWidth: "100%",
      }
    },
  };

  /**
   * Load the device enum template data to show device icon
   */
  useEffect(() => {
    const loadDeviceEnum = async () => {
      if (device.deviceEnumId) {
        try {
          const docRef = doc(firebaseStore, "deviceEnums", device.deviceEnumId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setDeviceEnum({ ...docSnap.data(), id: docSnap.id });
          }
        } catch (error) {
          console.error("Error loading device enum:", error);
        }
      }
    };
    loadDeviceEnum();
  }, [device.deviceEnumId]);

  /**
   * Set up 10-second timeout for image loading
   * If image doesn't load within 10 seconds, show fallback icon
   */
  useEffect(() => {
    if (!deviceEnum?.icon || imageLoaded) return;

    const timeout = setTimeout(() => {
      if (!imageLoaded) {
        setShowImageFallback(true);
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timeout);
  }, [deviceEnum?.icon, imageLoaded]);

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  /**
   * Deletes the registered device from Firestore
   * Does not delete associated images as they belong to the deviceEnum template
   */
  const handleDelete = async () => {
    try {
      setIsDialogOpen(false);
      await deleteDoc(doc(firebaseStore, "devices", device.id));
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing document: ", error);
    }
  };

  /**
   * Format timestamp to readable date
   */
  const formatDate = (timestamp: number) => {
    return moment(timestamp).format("MMM D, YYYY");
  };

  /**
   * Format timestamp to relative time (e.g., "2 hours ago")
   */
  const formatRelativeTime = (timestamp: number) => {
    return moment(timestamp).fromNow();
  };

  /**
   * Get status color and icon
   */
  const getStatusDisplay = () => {
    if (device.status === "active") {
      return {
        color: "success" as const,
        icon: <CheckCircleIcon fontSize="small" />,
        label: "Active"
      };
    } else {
      return {
        color: "warning" as const,
        icon: <CancelIcon fontSize="small" />,
        label: "Inactive"
      };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <>
      <Paper sx={style.paper} elevation={2}>
        {/* Header Section with Icon and Name */}
        <Box display="flex" gap={0} marginBottom={2}>
          {/* Device Icon */}
          <Box 
            sx={{ 
              width: "120px", 
              height: "120px", 
              flexShrink: 0,
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #e0e0e0",
              backgroundColor: "#f5f5f5",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {/* Fallback RouterIcon - always rendered as background */}
            <RouterIcon sx={{ fontSize: 60, color: "#999", position: "absolute" }} />
            
            {/* Device Image - overlays the fallback icon when available */}
            {deviceEnum?.icon && !showImageFallback && (
              <img
                src={deviceEnum.icon}
                alt={device.name}
                onLoad={() => setImageLoaded(true)}
                onError={() => setShowImageFallback(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: "8px",
                  position: "relative",
                  zIndex: 1,
                  backgroundColor: "#f5f5f5"
                }}
              />
            )}
          </Box>

          {/* Device Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {device.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontFamily: "monospace", 
                fontSize: "0.85rem",
                marginBottom: 1,
                wordBreak: "break-all"
              }}
            >
              ID: {device.deviceId}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", marginBottom: 1 }}>
              <Chip 
                label={device.deviceEnumType || "Unknown Type"} 
                size="small" 
                sx={{ fontSize: "0.75rem" }}
              />
              <Chip 
                label={device.os || "Unknown OS"} 
                size="small" 
                color="primary"
                sx={{ fontSize: "0.75rem" }}
              />
              <Chip 
                icon={statusDisplay.icon}
                label={statusDisplay.label} 
                size="small" 
                color={statusDisplay.color}
                sx={{ fontSize: "0.75rem" }}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Tooltip title="Edit Device">
              <IconButton
                size="small"
                onClick={() => setIsDialogOpen(true)}
                sx={{ color: "#44a99a" }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Device">
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ color: "#ff6b6b" }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        {/* Device Details Grid */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* MAC Address */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginBottom: 0.5 }}>
              <FingerprintIcon sx={{ fontSize: 16, color: "#666" }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                MAC Address
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: "monospace", 
                fontSize: "0.9rem",
                paddingLeft: 2.5 
              }}
            >
              {device.macAddress}
            </Typography>
          </Box>

          {/* Firmware Version */}
          {device.firmwareVersion && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Firmware
              </Typography>
              <Typography variant="body2" sx={{ paddingLeft: 2.5 }}>
                {device.firmwareVersion}
              </Typography>
            </Box>
          )}

          {/* Sensors and I/O */}
          {(device.sensors?.length > 0 || device.io?.length > 0) && (
            <Box sx={{ display: "flex", gap: 2, paddingLeft: 0 }}>
              {device.sensors?.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SensorsIcon sx={{ fontSize: 16, color: "#666" }} />
                  <Typography variant="body2" color="text.secondary">
                    {device.sensors.length} Sensor{device.sensors.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
              {device.io?.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SettingsInputComponentIcon sx={{ fontSize: 16, color: "#666" }} />
                  <Typography variant="body2" color="text.secondary">
                    {device.io.length} I/O
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ marginY: 1 }} />

          {/* Timestamps */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Registration Date */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarMonthIcon sx={{ fontSize: 16, color: "#666" }} />
              <Typography variant="caption" color="text.secondary">
                Registered: {formatDate(device.registeredAt)}
              </Typography>
            </Box>

            {/* Installation Date */}
            {device.installationDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarMonthIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" color="text.secondary">
                  Installed: {formatDate(device.installationDate)}
                </Typography>
              </Box>
            )}

            {/* Last Seen */}
            {device.lastSeen && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" color="text.secondary">
                  Last seen: {formatRelativeTime(device.lastSeen)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Notes */}
          {/*device.notes && (
            <>
              <Divider sx={{ marginY: 1 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ paddingLeft: 2.5, fontStyle: "italic" }}>
                  {device.notes}
                </Typography>
              </Box>
            </>
          )} */}
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <EditDevice
        device={device}
        isDialogOpen={isDialogOpen}
        handleClose={handleClose}
      />

      {/* Delete Confirmation Snackbar */}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
        message="Device deleted successfully"
      />
    </>
  );
}

export default CardsDeviceList;
